import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { PlaceholderBar } from '../../../../components/loading-skeleton/index';
import { sidebarRowPaddingClass } from '../../utils/sidebar-tree.util';

const SectionLabelPlaceholder: FC = () => (
  <PlaceholderBar className="h-3 w-28 rounded bg-gray-100" />
);

// Mirrors WorkspaceCard: kind label, icon + title / description, four stats, last updated.
const SpaceCardPlaceholder: FC = () => (
  <div className="flex w-full max-w-[418px] flex-col gap-4 rounded-lg bg-white px-3 py-3 shadow-sm">
    <div className="flex flex-col gap-3">
      <div className="flex h-[22px] items-center">
        <PlaceholderBar className="h-3 w-24 rounded bg-gray-100" />
      </div>
      <div className="flex items-center gap-2">
        <PlaceholderBar className="size-8 shrink-0 rounded bg-gray-100" />
        <div className="flex flex-1 flex-col gap-2">
          <PlaceholderBar className="h-4 w-36 rounded bg-gray-100" />
          <PlaceholderBar className="h-3 w-48 rounded bg-gray-100" />
        </div>
      </div>
    </div>
    <div className="flex w-full items-center">
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          className={twMerge('flex flex-1 flex-col gap-2', index < 3 && 'border-r border-gray-100')}
          key={index}
        >
          <PlaceholderBar className="h-4 w-10 rounded bg-gray-100" />
          <PlaceholderBar className="h-3 w-16 rounded bg-gray-100" />
        </div>
      ))}
    </div>
    <PlaceholderBar className="h-3 w-32 rounded bg-gray-100" />
  </div>
);

const SpaceSectionPlaceholder: FC<{ cards: number }> = ({ cards }) => (
  <>
    <SectionLabelPlaceholder />
    <div className="flex flex-wrap gap-4">
      {Array.from({ length: cards }).map((_, index) => (
        <SpaceCardPlaceholder key={index} />
      ))}
    </div>
  </>
);

export const WorkspacesHomePlaceholder: FC = () => (
  <div className="flex animate-pulse flex-col gap-4 px-4 py-4">
    <SpaceSectionPlaceholder cards={3} />
    <SpaceSectionPlaceholder cards={2} />
  </div>
);

const SIDEBAR_PLACEHOLDER_ROWS = 3;
// h-9 per row.
const SIDEBAR_ROW_HEIGHT_PX = 36;

// The rows that replace a loading level grow from exactly this height.
export const SIDEBAR_ROWS_PLACEHOLDER_HEIGHT_PX = SIDEBAR_PLACEHOLDER_ROWS * SIDEBAR_ROW_HEIGHT_PX;

export const SidebarRowsPlaceholder: FC<{ depth: number }> = ({ depth }) => (
  <div className="flex w-full animate-pulse flex-col">
    {Array.from({ length: SIDEBAR_PLACEHOLDER_ROWS }).map((_, index) => (
      <div
        className={twMerge('flex h-9 w-full items-center gap-2', sidebarRowPaddingClass(depth))}
        key={index}
      >
        <span className="size-4 shrink-0" />
        <PlaceholderBar className="size-4 shrink-0 rounded bg-gray-100" />
        <PlaceholderBar className="h-3 w-28 rounded bg-gray-100" />
      </div>
    ))}
  </div>
);

export const SidebarSectionPlaceholder: FC = () => (
  <div className="flex w-full animate-pulse flex-col gap-3">
    <SectionLabelPlaceholder />
    <SidebarRowsPlaceholder depth={0} />
  </div>
);

// Fixed columns of WorkspaceFilesTable after the name: type, last edit, size (each px-4), menu.
const BROWSER_COLUMN_CLASS = ['w-[109px] px-4', 'w-[127px] px-4', 'w-[97px] px-4'];

const BROWSER_PLACEHOLDER_ROWS = 5;

// Same columns and row heights as WorkspaceFilesTable (44px header, 75px rows), so nothing jumps.
export const BrowserTablePlaceholder: FC = () => (
  <div className="shrink-0 animate-pulse overflow-hidden rounded-lg border border-gray-100 bg-white">
    <div className="flex h-11 items-center bg-gray-50">
      <div className="flex-1 px-4">
        <PlaceholderBar className="h-3 w-12 rounded bg-gray-100" />
      </div>
      {BROWSER_COLUMN_CLASS.map((className) => (
        <div className={twMerge('shrink-0', className)} key={className}>
          <PlaceholderBar className="h-3 w-10 rounded bg-gray-100" />
        </div>
      ))}
      <span className="w-12 shrink-0" />
    </div>
    <div className="divide-y divide-[#F2F4F7]">
      {Array.from({ length: BROWSER_PLACEHOLDER_ROWS }).map((_, index) => (
        <div className="flex h-[75px] items-center" key={index}>
          <div className="flex min-w-0 flex-1 items-center gap-2 px-4">
            <PlaceholderBar className="size-8 shrink-0 rounded bg-gray-100" />
            <PlaceholderBar className="h-4 w-40 rounded bg-gray-100" />
          </div>
          {BROWSER_COLUMN_CLASS.map((className) => (
            <div className={twMerge('shrink-0', className)} key={className}>
              <PlaceholderBar className="h-3 w-12 rounded bg-gray-100" />
            </div>
          ))}
          <div className="flex w-12 shrink-0 justify-center">
            <PlaceholderBar className="size-4 rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const MOBILE_LIST_PLACEHOLDER_ROWS = 4;

export const MobileListPlaceholder: FC = () => (
  <div className="animate-pulse overflow-hidden rounded-lg border border-gray-100 bg-white">
    {Array.from({ length: MOBILE_LIST_PLACEHOLDER_ROWS }).map((_, index) => (
      <div
        className="flex h-[72px] items-center gap-2 border-b border-gray-100 p-4 last:border-b-0"
        key={index}
      >
        <PlaceholderBar className="size-8 shrink-0 rounded bg-gray-100" />
        <PlaceholderBar className="h-4 w-full max-w-40 rounded bg-gray-100" />
      </div>
    ))}
  </div>
);
